import { type TiersConfig } from "@clipboard-health/flag-attendance-policy";
import { type Tier, type TierType } from "@src/appV2/redesign/Tiers/api/schema";
import { calculateRating } from "@src/appV2/redesign/Tiers/utils/calculateRating";
import {
  type NormalizedRatings,
  type NormalizedTierRatings,
} from "@src/appV2/redesign/Tiers/utils/types";

// No default clause or consistent return as it should be exhaustive
// eslint-disable-next-line consistent-return
function getTargetTier(workerTier: TierType): Exclude<TierType, "LIMITED"> {
  // eslint-disable-next-line default-case
  switch (workerTier) {
    case "GOLD":
    case "SILVER": {
      return "GOLD";
    }

    case "LIMITED": {
      return "SILVER";
    }
  }
}

// No default clause or consistent return as it should be exhaustive
// eslint-disable-next-line consistent-return
function getTargetTierRatings(
  targetTier: Exclude<TierType, "LIMITED">,
  normalizedTierRatingsWithoutTarget: Omit<NormalizedTierRatings, "targetRatings">
): NormalizedRatings {
  // eslint-disable-next-line default-case
  switch (targetTier) {
    case "GOLD": {
      return normalizedTierRatingsWithoutTarget.goldTierRatings;
    }

    case "SILVER": {
      return normalizedTierRatingsWithoutTarget.silverTierRatings;
    }
  }
}

export function buildNormalizedTierRatings(
  tier: Tier,
  tierConfiguration: Extract<TiersConfig, { enabled: true }>
): NormalizedTierRatings {
  const {
    attributes: { type: workerTier, criteria: workerTierCriteria },
  } = tier;
  const targetTier = getTargetTier(workerTier);
  const goldTierCriteria = tierConfiguration.tiers[0].criteria;
  const silverTierCriteria = tierConfiguration.tiers[1].criteria;

  const normalizedTierRatingsWithoutTarget = {
    eventsToCount: tierConfiguration.eventsToCount,
    workerTier,
    targetTier,
    workerRatings: {
      shiftCompletionWithoutCancel: calculateRating(
        "shiftCompletionWithoutCancel",
        workerTierCriteria.shiftCompletionsWithoutCancel.successes,
        workerTierCriteria.shiftCompletionsWithoutCancel.totalEvents
      ),
      shiftCompletionWithoutLateCancel: calculateRating(
        "shiftCompletionWithoutLateCancel",
        workerTierCriteria.lateCancels.recentLateCancels,
        workerTierCriteria.lateCancels.totalEvents
      ),
      onTimeArrival: calculateRating(
        "onTimeArrival",
        workerTierCriteria.onTimeArrivals.successes,
        workerTierCriteria.onTimeArrivals.totalEvents
      ),
    },
    goldTierRatings: {
      shiftCompletionWithoutCancel: calculateRating(
        "shiftCompletionWithoutCancel",
        goldTierCriteria.shiftCompletionsWithoutCancel,
        tierConfiguration.eventsToCount
      ),
      shiftCompletionWithoutLateCancel: calculateRating(
        "shiftCompletionWithoutLateCancel",
        goldTierCriteria.recentLateCancels,
        tierConfiguration.eventsToCount
      ),
      onTimeArrival: calculateRating(
        "onTimeArrival",
        goldTierCriteria.onTimeArrivals,
        tierConfiguration.eventsToCount
      ),
    },
    silverTierRatings: {
      shiftCompletionWithoutCancel: calculateRating(
        "shiftCompletionWithoutCancel",
        silverTierCriteria.shiftCompletionsWithoutCancel,
        tierConfiguration.eventsToCount
      ),
      shiftCompletionWithoutLateCancel: calculateRating(
        "shiftCompletionWithoutLateCancel",
        silverTierCriteria.recentLateCancels,
        tierConfiguration.eventsToCount
      ),
      onTimeArrival: calculateRating(
        "onTimeArrival",
        silverTierCriteria.onTimeArrivals,
        tierConfiguration.eventsToCount
      ),
    },
  };

  return {
    ...normalizedTierRatingsWithoutTarget,
    targetRatings: getTargetTierRatings(targetTier, normalizedTierRatingsWithoutTarget),
  };
}
